"use client";

import {
  useMemo,
  useState,
  useEffect,
  type ReactElement,
  type ElementType,
} from "react";
import {
  cn,
  Button,
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandGroup,
  CommandItem,
  DialogTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Badge,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "../..";
import {
  Search,
  MapPinned,
  Binoculars,
  ChartLine,
  Handshake,
  Images,
  BookMarked,
  Pin,
  ClipboardList,
  Map,
  SquareCode,
  Waves,
  CloudSun,
} from "lucide-react";
import {
  applications,
  type Application,
  type Tag,
  type Observatory,
} from "../../services";
import { cva } from "class-variance-authority";
import { useTranslations, useLocale } from "use-intl";
import Fuse from "fuse.js";
import type { Locale } from "../../i18n/config.ts";

interface PortalHeaderSearchProps {
  observatories: Observatory[];
  tags: Tag[];
}

const Icons: Record<string, ElementType> = {
  Search,
  MapPinned,
  Binoculars,
  ChartLine,
  Handshake,
  Images,
  BookMarked,
  Pin,
  ClipboardList,
  Map,
  SquareCode,
  Waves,
  CloudSun,
};

const tabVariants = cva(
  "flex-1 flex flex-row items-center justify-between pl-3 pr-2 rounded-full bg-accent [&[data-state='active']]:bg-blue-700 [&[data-state='active']]:text-white",
);

function fuseSearch<T>(fuse: Fuse<T>, list: T[], query: string) {
  if (!query.trim()) {
    return list.map((item) => ({ item }));
  }
  return fuse.search(query);
}

function PortalHeaderSearch({
  observatories,
  tags,
}: Readonly<PortalHeaderSearchProps>): ReactElement | null {
  const locale: Locale = useLocale() as Locale;

  const alphabetizedObservatories = observatories
    .filter((observatory) => observatory.name[locale] !== undefined)
    .toSorted((a, b) => a.name[locale].localeCompare(b.name[locale]));
  const alphabetizedTags = tags
    .filter((tag) => tag.name[locale] !== undefined)
    .toSorted((a, b) => a.name[locale].localeCompare(b.name[locale]));
  const alphabetizedApplications = applications
    .filter((application) => application.title[locale] !== undefined)
    .toSorted((a, b) => a.title[locale].localeCompare(b.title[locale]));

  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("observatories");
  const [searchQuery, setSearchQuery] = useState("");
  const t = useTranslations("global.HeaderSearch");

  const fuseObservatories = useMemo(() => {
    return new Fuse(alphabetizedObservatories, {
      keys: ["id", "name.fr", "name.en"], // fields to search
      threshold: 0.4, // 0 = exact, 1 = match anything
    });
  }, []);

  const fuseTags = useMemo(() => {
    return new Fuse(alphabetizedTags, {
      keys: ["id", "name.fr", "name.en"], // fields to search
      threshold: 0.4, // 0 = exact, 1 = match anything
    });
  }, []);

  const fuseApps = useMemo(() => {
    return new Fuse(alphabetizedApplications, {
      keys: ["id", "name.fr", "name.en"], // fields to search
      threshold: 0.4, // 0 = exact, 1 = match anything
    });
  }, []);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const handleObservatorySelect = (observatory: Observatory) => {
    if (observatory.portalURL) {
      globalThis.location.assign(observatory.portalURL);
    }
  };

  const handleApplicationSelect = (application: Application) => {
    if (application.url) {
      globalThis.location.assign(application.url);
    }
  };

  const handleSearchQuery = (query: string) => {
    setSearchQuery(query);
  };

  const filteredObservatories = fuseSearch(
    fuseObservatories,
    alphabetizedObservatories,
    searchQuery,
  );
  const filteredTags = fuseSearch(fuseTags, alphabetizedTags, searchQuery);
  const filteredApplications = fuseSearch(fuseApps, applications, searchQuery);

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              className="xl:hidden size-8 rounded-full"
              size="icon"
              variant="outline"
              aria-label={t("title")}
              onClick={() => {
                setOpen(true);
              }}
            >
              <Search strokeWidth={1} size={16} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t("title")}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <Button
        className="hidden xl:inline-flex gap-2 text-muted-foreground"
        onClick={() => {
          setOpen(true);
        }}
        variant="outline"
      >
        <Search strokeWidth={1} size={16} />
        {t("title")}
      </Button>
      <Command className="p-0" shouldFilter={false}>
        <CommandDialog
          onOpenChange={setOpen}
          open={open}
          className="font-inter md:min-w-xl min-w-0 gap-0"
        >
          <DialogTitle className="hidden">{t("title")}</DialogTitle>
          <CommandInput
            className="block w-full min-w-0 text-base not-italic truncate"
            placeholder={t("search")}
            value={searchQuery}
            onValueChange={handleSearchQuery}
          />
          <Tabs
            defaultValue="observatories"
            className="flex flex-col gap-0"
            value={tab}
            onValueChange={setTab}
          >
            <TabsList className="w-full gap-2 bg-transparent rounded-full border-0 h-12">
              <TabsTrigger
                value="observatories"
                tabIndex={0}
                onFocus={() => setTab("observatories")}
                className={cn(tabVariants())}
              >
                {t("observatories")}
                <Badge className="border-0">
                  {filteredObservatories.length}
                </Badge>
              </TabsTrigger>
              <TabsTrigger
                value="tags"
                tabIndex={0}
                onFocus={() => setTab("tags")}
                className={cn(tabVariants())}
              >
                {t("glossary")}
                <Badge className="border-0">{filteredTags.length}</Badge>
              </TabsTrigger>
              <TabsTrigger
                value="applications"
                tabIndex={0}
                onFocus={() => setTab("applications")}
                className={cn(tabVariants())}
              >
                {t("applications")}
                <Badge className="border-0">
                  {filteredApplications.length}
                </Badge>
              </TabsTrigger>
            </TabsList>
            <TabsContent
              value="observatories"
              className="mt-0 border-t data-[state=inactive]:hidden"
            >
              <CommandList>
                {filteredObservatories.length ? (
                  <CommandGroup heading={t("observatories")} forceMount>
                    {filteredObservatories.map((observatory) => {
                      return (
                        <CommandItem
                          key={`obs-${observatory.item.id}`}
                          value={`obs-${observatory.item.id}`}
                          onSelect={() =>
                            handleObservatorySelect(observatory.item)
                          }
                        >
                          <div key={observatory.item.id} className="space-y-2">
                            <h5 className="text-sm font-normal leading-tight p-0 mb-1">
                              {observatory.item.name[locale]}
                            </h5>
                            {observatory.item.description?.[locale] ? (
                              <p className="text-xs text-muted-foreground leading-none p-0 m-0">
                                {observatory.item.description[locale]}
                              </p>
                            ) : null}
                          </div>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                ) : (
                  <div className="p-4 text-center">{t("noResultsFound")}</div>
                )}
              </CommandList>
            </TabsContent>
            <TabsContent
              value="applications"
              className="mt-0 border-t data-[state=inactive]:hidden"
            >
              <CommandList>
                {filteredApplications.length ? (
                  <CommandGroup
                    heading={t("applications")}
                    className="gap-2"
                    forceMount
                  >
                    {!!filteredApplications.length &&
                      filteredApplications.map((application) => {
                        const Icon = Icons[
                          application.item.icon
                        ] as ElementType;

                        return (
                          <CommandItem
                            className="flex flex-row items-start gap-3"
                            key={`app-${application.item.id}`}
                            value={`app-${application.item.id}`}
                            onSelect={() =>
                              handleApplicationSelect(application.item)
                            }
                          >
                            <Icon size={16} strokeWidth={1} />
                            <div className="flex flex-col space-y-2">
                              <h5 className="text-sm font-normal leading-tight p-0 mb-1">
                                {application.item.title[locale]}
                              </h5>
                            </div>
                          </CommandItem>
                        );
                      })}
                  </CommandGroup>
                ) : (
                  <div className="p-4 text-center">{t("noResultsFound")}</div>
                )}
              </CommandList>
            </TabsContent>
            <TabsContent
              value="tags"
              className="mt-0 border-t data-[state=inactive]:hidden"
            >
              <CommandList>
                {filteredTags.length ? (
                  <CommandGroup heading={t("glossary")} forceMount>
                    {filteredTags.map((tag) => {
                      return (
                        <CommandItem
                          key={`tag-${tag.item.id}`}
                          value={`tag-${tag.item.id}`}
                        >
                          <div className="space-y-2">
                            <h5 className="text-sm font-normal leading-tight p-0 mb-1">
                              {tag.item.name[locale]}
                            </h5>
                            {tag.item.description?.[locale] ? (
                              <p className="text-xs text-muted-foreground leading-none p-0 m-0">
                                {tag.item.description[locale]}
                              </p>
                            ) : null}
                          </div>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                ) : (
                  <div className="p-4 text-center">{t("noResultsFound")}</div>
                )}
              </CommandList>
            </TabsContent>
          </Tabs>
        </CommandDialog>
      </Command>
    </>
  );
}

export { PortalHeaderSearch };
